/**
 * Permission checking hook
 * Provides convenient methods for checking user permissions throughout the app
 */

import { useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, hasAdminPermissions } from '../utils/permissions';

/**
 * Return type for usePermission hook
 */
export interface UsePermissionReturn {
  /**
   * Check if user has a specific permission
   * Supports wildcard matching (e.g., 'product:*' matches 'product:admin')
   *
   * @param permission - Permission string to check (e.g., 'product:read', 'product:admin')
   * @returns True if user has the permission
   *
   * @example
   * ```typescript
   * const { can } = usePermission();
   * if (can('product:create')) {
   *   // Show create button
   * }
   * ```
   */
  can: (permission: string) => boolean;

  /**
   * Check if user has ANY of the specified permissions
   *
   * @param permissions - Array of permissions to check
   * @returns True if user has at least one of the permissions
   *
   * @example
   * ```typescript
   * const { canAny } = usePermission();
   * if (canAny(['product:update', 'product:delete'])) {
   *   // Show edit menu
   * }
   * ```
   */
  canAny: (permissions: string[]) => boolean;

  /**
   * Check if user has ALL of the specified permissions
   *
   * @param permissions - Array of permissions to check
   * @returns True if user has all of the permissions
   *
   * @example
   * ```typescript
   * const { canAll } = usePermission();
   * if (canAll(['product:update', 'product:delete'])) {
   *   // Show advanced admin menu
   * }
   * ```
   */
  canAll: (permissions: string[]) => boolean;

  /**
   * Check if user has any admin-level permissions
   * Returns true if user has any :admin or :* permissions
   *
   * @returns True if user has admin permissions
   *
   * @example
   * ```typescript
   * const { hasAnyAdminPermission } = usePermission();
   * if (hasAnyAdminPermission()) {
   *   // Show admin dashboard link
   * }
   * ```
   */
  hasAnyAdminPermission: () => boolean;

  /**
   * Check if user is a super admin (has admin:* permission)
   */
  isSuperAdmin: boolean;

  /**
   * Raw user permissions array
   */
  permissions: string[];
}

/**
 * usePermission hook - Check user permissions with convenience methods
 *
 * @returns {UsePermissionReturn} Permission checking utilities
 *
 * @example
 * ```typescript
 * const { can, canAny, canAll, hasAnyAdminPermission, isSuperAdmin } = usePermission();
 *
 * // Check single permission
 * if (can('product:create')) {
 *   // Show create button
 * }
 *
 * // Check if user can access admin dashboard
 * if (hasAnyAdminPermission()) {
 *   // Show dashboard link
 * }
 *
 * // Check specific admin access
 * if (can('product:admin')) {
 *   // Show products tab in admin
 * }
 * ```
 */
export function usePermission(): UsePermissionReturn {
  const { userData } = useAuth();

  // DEV OVERRIDE: Grant all permissions locally when no claims available
  const isDev = import.meta.env.DEV;
  const permissions = isDev && (!userData?.permissions || userData.permissions.length === 0)
    ? ['admin:*', 'product:*', 'order:*', 'user:*', 'analytics:*', 'settings:*', 'help:*']
    : (userData?.permissions || []);

  // Memoize permission checks for performance
  const can = useCallback(
    (permission: string): boolean => {
      return hasPermission(permissions, permission);
    },
    [permissions]
  );

  const canAny = useCallback(
    (requiredPermissions: string[]): boolean => {
      return requiredPermissions.some((perm) => hasPermission(permissions, perm));
    },
    [permissions]
  );

  const canAll = useCallback(
    (requiredPermissions: string[]): boolean => {
      return requiredPermissions.every((perm) => hasPermission(permissions, perm));
    },
    [permissions]
  );

  const hasAnyAdminPermission = useCallback((): boolean => {
    return hasAdminPermissions(permissions);
  }, [permissions]);

  const isSuperAdmin = useMemo(() => {
    return permissions.includes('admin:*');
  }, [permissions]);

  return {
    can,
    canAny,
    canAll,
    hasAnyAdminPermission,
    isSuperAdmin,
    permissions,
  };
}
